/**
 * AgentToolCallsBar - Collapsible bar at bottom showing tools used + results
 * Compact by default; expands upward on click (Polsia-style)
 */

import React, { useState } from "react";
import "./AgentToolCallsBar.css";

export interface ToolPair {
    call: {
        tool_name?: string;
        tool_input?: Record<string, unknown>;
    };
    result?: {
        tool_output?: string;
    };
}

export interface AgentToolCallsBarProps {
    toolPairs: ToolPair[];
    darkMode?: boolean;
}

function parseToolOutput(output: string): { success?: boolean; excerpt?: string } {
    try {
        const parsed = JSON.parse(output);
        if (parsed && typeof parsed === "object") {
            const success = parsed.success;
            let excerpt = "";
            if (parsed.results && Array.isArray(parsed.results)) {
                excerpt = parsed.results
                    .slice(0, 2)
                    .map((r: { title?: string; content?: string; snippet?: string }) =>
                        r.content || r.snippet || r.title || ""
                    )
                    .join(" ")
                    .slice(0, 500);
            } else if (parsed.content) {
                excerpt = String(parsed.content).slice(0, 500);
            } else if (parsed.error) {
                excerpt = `Error: ${String(parsed.error)}`;
            }
            return { success, excerpt: excerpt || output.slice(0, 500) };
        }
    } catch {
        // not JSON
    }
    return { excerpt: output.slice(0, 600) };
}

export function buildToolPairsFromLogs(logs: { type: string; tool_name?: string; tool_input?: Record<string, unknown>; tool_output?: string }[]): ToolPair[] {
    const pairs: ToolPair[] = [];
    for (let i = 0; i < logs.length; i++) {
        if (logs[i].type === "tool_call") {
            const result = logs[i + 1]?.type === "tool_result" && logs[i + 1].tool_name === logs[i].tool_name
                ? logs[i + 1]
                : undefined;
            pairs.push({ call: logs[i], result });
        }
    }
    return pairs;
}

export const AgentToolCallsBar: React.FC<AgentToolCallsBarProps> = ({
    toolPairs,
    darkMode = false,
}) => {
    const [expanded, setExpanded] = useState(false);

    if (toolPairs.length === 0) return null;

    const toolNames = toolPairs.map((p) => (p.call.tool_name || "?").replace(/_/g, " "));
    const summary = toolNames.length <= 2
        ? toolNames.join(", ")
        : `${toolNames.slice(0, 2).join(", ")} +${toolNames.length - 2} more`;

    return (
        <div className={`agent-tool-calls-bar ${darkMode ? "dark" : ""} ${expanded ? "expanded" : ""}`}>
            <button
                type="button"
                className="agent-tool-calls-bar-trigger"
                onClick={() => setExpanded(!expanded)}
                aria-expanded={expanded}
            >
                <span className="agent-tool-calls-bar-icon">🔧</span>
                <span className="agent-tool-calls-bar-summary">
                    {toolPairs.length} tool{toolPairs.length !== 1 ? "s" : ""} used: {summary}
                </span>
                <span className="agent-tool-calls-bar-chevron">{expanded ? "▼" : "▲"}</span>
            </button>

            {expanded && (
                <div className="agent-tool-calls-bar-panel">
                    <div className="agent-tool-calls-bar-panel-inner">
                        {toolPairs.map(({ call, result }, i) => {
                            const name = call.tool_name || "?";
                            const input = call.tool_input;
                            const inputStr = input?.query
                                ? `query: "${String(input.query).slice(0, 100)}${String(input.query).length > 100 ? "…" : ""}"`
                                : input
                                    ? JSON.stringify(input).slice(0, 150)
                                    : "";
                            const { success, excerpt } = result?.tool_output
                                ? parseToolOutput(result.tool_output)
                                : {};

                            return (
                                <div key={i} className="agent-tool-calls-bar-item">
                                    <div className="agent-tool-calls-bar-item-header">
                                        <span className="agent-tool-calls-bar-item-name">
                                            {name.replace(/_/g, " ")}
                                        </span>
                                        {success !== undefined && (
                                            <span className={`agent-tool-calls-bar-item-ok ${success ? "ok" : "fail"}`}>
                                                {success ? "✓" : "✗"}
                                            </span>
                                        )}
                                    </div>
                                    {inputStr && (
                                        <div className="agent-tool-calls-bar-item-input">
                                            <span className="label">Input:</span> {inputStr}
                                        </div>
                                    )}
                                    {excerpt && (
                                        <div className="agent-tool-calls-bar-item-output">
                                            <span className="label">Result:</span>
                                            <pre>{excerpt}{excerpt.length >= 500 ? "…" : ""}</pre>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
